<template>
  <div>
    <h2 class="mb-3">
      Flagged Calls
    </h2>

    <!-- Statistics -->
    <CCard
      v-if="statistics"
      class="mb-3 shadow-sm"
    >
      <CCardHeader
        :class="['bg-white d-flex align-items-center', { 'border-bottom-0': !statisticsExpanded }]"
        style="cursor: pointer;"
        @click="statisticsExpanded = !statisticsExpanded"
      >
        <span>
          <strong>Statistics</strong>
          <CIcon
            :name="statisticsExpanded ? 'cil-chevron-top' : 'cil-chevron-bottom'"
            :class="['ml-2', statisticsExpanded ? 'text-muted' : 'text-success']"
            size="sm"
          />
        </span>
      </CCardHeader>
      <CCollapse :show="statisticsExpanded">
        <CCardBody>
          <!-- KPI Cards -->
          <div class="row mb-4">
            <div class="col-sm-6 col-lg-3">
              <CCard class="mb-3 mb-lg-0 border-0 shadow-sm overflow-hidden">
                <div class="p-3 d-flex align-items-center bg-gradient-info text-white h-100">
                  <div class="mr-3">
                    <CIcon
                      name="cil-list-numbered"
                      height="36"
                    />
                  </div>
                  <div>
                    <div class="text-value-lg">
                      {{ statistics.totalCount }}
                    </div>
                    <div class="text-uppercase font-weight-bold small">
                      Total Flagged
                    </div>
                  </div>
                </div>
              </CCard>
            </div>

            <div class="col-sm-6 col-lg-3">
              <CCard class="mb-3 mb-lg-0 border-0 shadow-sm overflow-hidden">
                <div class="p-3 d-flex align-items-center bg-gradient-danger text-white h-100">
                  <div class="mr-3">
                    <CIcon
                      name="cil-warning"
                      height="36"
                    />
                  </div>
                  <div>
                    <div class="text-value-lg">
                      {{ statistics.fraudCount }}
                    </div>
                    <div class="text-uppercase font-weight-bold small">
                      Fraud
                    </div>
                  </div>
                </div>
              </CCard>
            </div>

            <div class="col-sm-6 col-lg-3">
              <CCard class="mb-3 mb-lg-0 border-0 shadow-sm overflow-hidden">
                <div class="p-3 d-flex align-items-center bg-gradient-warning text-white h-100">
                  <div class="mr-3">
                    <CIcon
                      name="cil-bell"
                      height="36"
                    />
                  </div>
                  <div>
                    <div class="text-value-lg">
                      {{ statistics.suspiciousCount }}
                    </div>
                    <div class="text-uppercase font-weight-bold small">
                      Suspicious
                    </div>
                  </div>
                </div>
              </CCard>
            </div>

            <div class="col-sm-6 col-lg-3">
              <CCard class="mb-3 mb-lg-0 border-0 shadow-sm overflow-hidden">
                <div class="p-3 d-flex align-items-center bg-gradient-dark text-white h-100">
                  <div class="mr-3">
                    <CIcon
                      name="cil-bell-exclamation"
                      height="36"
                    />
                  </div>
                  <div>
                    <div class="text-value-lg">
                      {{ statistics.importantCount }}
                    </div>
                    <div class="text-uppercase font-weight-bold small">
                      Important
                    </div>
                    <div
                      class="small"
                      style="font-size: 0.65rem; opacity: 0.85;"
                    >
                      Slack notification sent
                    </div>
                  </div>
                </div>
              </CCard>
            </div>
          </div>

          <!-- Charts Row -->
          <div class="row">
            <!-- Top Sellers by Fraud -->
            <div class="col-md-4">
              <CCard class="h-100 mb-3 shadow-sm">
                <CCardHeader class="bg-white">
                  <strong>Top Sellers</strong>
                </CCardHeader>
                <CCardBody>
                  <div
                    v-if="statistics.topSellersByFraudCount && statistics.topSellersByFraudCount.length > 0"
                  >
                    <div
                      v-for="(seller, index) in statistics.topSellersByFraudCount.slice(0, 10)"
                      :key="index"
                      class="d-flex justify-content-between align-items-center mb-2"
                    >
                      <span class="text-truncate mr-2">{{ seller.sellerName }}</span>
                      <div class="d-flex align-items-center text-nowrap">
                        <CBadge
                          color="danger"
                          class="mr-1"
                        >
                          {{ seller.fraudCount }}
                        </CBadge>
                        <CBadge
                          color="warning"
                          class="mr-1"
                        >
                          {{ seller.suspiciousCount }}
                        </CBadge>
                        <CBadge color="dark">
                          {{ seller.importantCount }}
                        </CBadge>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-center text-muted p-4"
                  >
                    No data
                  </div>
                </CCardBody>
              </CCard>
            </div>

            <!-- Top Categories -->
            <div class="col-md-4">
              <CCard class="h-100 mb-3 shadow-sm">
                <CCardHeader class="bg-white">
                  <strong>Top Categories</strong>
                </CCardHeader>
                <CCardBody>
                  <div
                    v-if="statistics.topCategories && statistics.topCategories.length > 0"
                  >
                    <div
                      v-for="(cat, index) in statistics.topCategories.slice(0, 10)"
                      :key="index"
                      class="d-flex justify-content-between align-items-center mb-2"
                    >
                      <span class="text-truncate mr-2">{{ cat.categoryName }}</span>
                      <div class="d-flex align-items-center text-nowrap">
                        <CBadge
                          color="danger"
                          class="mr-1"
                        >
                          {{ cat.fraudCount }}
                        </CBadge>
                        <CBadge
                          color="warning"
                          class="mr-1"
                        >
                          {{ cat.suspiciousCount }}
                        </CBadge>
                        <CBadge color="dark">
                          {{ cat.importantCount }}
                        </CBadge>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-center text-muted p-4"
                  >
                    No data
                  </div>
                </CCardBody>
              </CCard>
            </div>

            <!-- Filter Tag Breakdown -->
            <div class="col-md-4">
              <CCard class="h-100 mb-3 shadow-sm">
                <CCardHeader class="bg-white">
                  <strong>By Filter Tag</strong>
                </CCardHeader>
                <CCardBody>
                  <div
                    v-if="statistics.byFilterTag && statistics.byFilterTag.length > 0"
                  >
                    <div
                      v-for="(tag, index) in statistics.byFilterTag.slice(0, 15)"
                      :key="index"
                      class="d-flex justify-content-between align-items-center mb-2"
                    >
                      <span class="text-truncate mr-2">{{ formatTag(tag.tag) }}</span>
                      <div class="d-flex align-items-center text-nowrap">
                        <CBadge
                          color="danger"
                          class="mr-1"
                        >
                          {{ tag.fraudCount }}
                        </CBadge>
                        <CBadge
                          color="warning"
                          class="mr-1"
                        >
                          {{ tag.suspiciousCount }}
                        </CBadge>
                        <CBadge color="dark">
                          {{ tag.importantCount }}
                        </CBadge>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-center text-muted p-4"
                  >
                    No data
                  </div>
                </CCardBody>
              </CCard>
            </div>
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>

    <!-- Results Table -->
    <CCard>
      <CCardBody>
        <!-- Filters -->
        <div class="row mb-3 filters-row">
          <div class="col-auto d-flex flex-column justify-content-end">
            <label
              class="small font-weight-bold text-nowrap"
              style="margin-top: 3px;"
            >
              <CIcon
                v-c-tooltip="'Confirmed fraud or source channel is Outbound Dial / SMS Response'"
                name="cil-info"
                size="sm"
                class="text-muted"
                style="cursor: help;"
              />
              Important only
            </label>
            <div
              class="d-flex align-items-center"
              style="height: 31px;"
            >
              <CSwitch
                color="primary"
                shape="pill"
                size="sm"
                :checked.sync="showImportantOnly"
              />
            </div>
          </div>
          <div class="col">
            <label class="small font-weight-bold">Type</label>
            <select
              v-model="filterType"
              class="form-control form-control-sm"
            >
              <option value="">
                All
              </option>
              <option
                v-for="opt in typeOptions"
                :key="opt"
                :value="opt"
              >
                {{ opt }}
              </option>
            </select>
          </div>
          <div class="col">
            <label class="small font-weight-bold">Seller</label>
            <select
              v-model="filterSeller"
              class="form-control form-control-sm"
            >
              <option value="">
                All
              </option>
              <option
                v-for="opt in sellerOptions"
                :key="opt"
                :value="opt"
              >
                {{ opt }}
              </option>
            </select>
          </div>
          <div class="col">
            <label class="small font-weight-bold">Buyer</label>
            <select
              v-model="filterBuyer"
              class="form-control form-control-sm"
            >
              <option value="">
                All
              </option>
              <option
                v-for="opt in buyerOptions"
                :key="opt"
                :value="opt"
              >
                {{ opt }}
              </option>
            </select>
          </div>
          <div class="col">
            <label class="small font-weight-bold">Category</label>
            <select
              v-model="filterCategory"
              class="form-control form-control-sm"
            >
              <option value="">
                All
              </option>
              <option
                v-for="opt in categoryOptions"
                :key="opt"
                :value="opt"
              >
                {{ opt }}
              </option>
            </select>
          </div>
          <div class="col">
            <label class="small font-weight-bold">Filter Tags</label>
            <div
              v-click-outside="() => { tagDropdownOpen = false }"
              class="position-relative"
            >
              <button
                type="button"
                class="form-control form-control-sm d-flex align-items-center justify-content-between text-left"
                @click="tagDropdownOpen = !tagDropdownOpen"
              >
                <span class="text-truncate">{{ filterTagsLabel }}</span>
                <CIcon
                  name="cil-chevron-bottom"
                  size="sm"
                />
              </button>
              <div
                v-show="tagDropdownOpen"
                class="position-absolute bg-white border rounded shadow-sm px-3 py-2 mt-1"
                style="max-height: 250px; overflow-y: auto; width: 100%; z-index: 1050;"
              >
                <div class="d-flex mb-2">
                  <a
                    href="#"
                    class="small mr-3"
                    @click.prevent="filterTags = [...tagOptions]"
                  >Select All</a>
                  <a
                    href="#"
                    class="small"
                    @click.prevent="filterTags = []"
                  >Clear All</a>
                </div>
                <div
                  v-for="tag in tagOptions"
                  :key="tag"
                  class="form-check mb-1"
                >
                  <input
                    :id="'tag-' + tag"
                    v-model="filterTags"
                    type="checkbox"
                    class="form-check-input"
                    :value="tag"
                  >
                  <label
                    :for="'tag-' + tag"
                    class="form-check-label small"
                  >{{ formatTag(tag) }}</label>
                </div>
              </div>
            </div>
          </div>
          <div class="col d-flex align-items-end justify-content-end">
            <button
              type="button"
              class="btn btn-sm btn-outline-dark mr-2"
              :disabled="!hasActiveFilters"
              @click="clearFilters"
            >
              <CIcon
                name="cil-x"
                size="sm"
                class="mr-1"
              />
              Clear
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              :disabled="loading"
              @click="handleFetch({ force: true })"
            >
              <CSpinner
                v-if="loading"
                size="sm"
                class="mr-1"
              />
              <CIcon
                v-else
                name="cil-sync"
                size="sm"
                class="mr-1"
              />
              Refresh
            </button>
          </div>
        </div>

        <CDataTable
          :items="filteredResults"
          :fields="fields"
          :loading="loading"
          hover
          striped
          border
          sorter
          :sorter-value="{ column: 'receivedAt', asc: false }"
          :items-per-page="10"
          :pagination="{ align: 'center' }"
        >
          <template #callId="{ item }">
            <td>
              <div class="d-flex align-items-center text-nowrap">
                <router-link
                  :to="{ name: 'FraudDetail', params: { id: item.callId } }"
                  :title="item.callId"
                  class="text-monospace"
                >
                  {{ truncateCallId(item.callId) }}
                </router-link>
                <CIcon
                  name="cil-copy"
                  class="ml-1 text-muted"
                  style="cursor: pointer;"
                  @click.native="copyCallId(item.callId)"
                />
                <a
                  v-if="item.notifyAdmins"
                  :href="getSlackLink(item.flagCategory)"
                  title="Search for this call ID in Slack channel"
                  class="ml-2"
                  style="margin-top: -3px;"
                >
                  <CIcon
                    name="cib-slack"
                    class="text-info"
                  />
                </a>
              </div>
              <div
                v-if="item.callerId"
                class="small text-muted"
              >
                {{ item.callerId }}
              </div>
            </td>
          </template>

          <template #receivedAt="{ item }">
            <td>
              {{ formatDateTime(item.receivedAt) }}
            </td>
          </template>

          <template #flagCategory="{ item }">
            <td>
              <CBadge :color="item.flagCategory === 'Fraud' || item.flagCategory === 0 ? 'danger' : 'warning'">
                {{ item.flagCategory === 0 ? 'Fraud' : item.flagCategory === 1 ? 'Suspicious' : item.flagCategory }}
              </CBadge>
            </td>
          </template>

          <template #filterTags="{ item }">
            <td>
              <span
                v-for="(tag, idx) in splitTags(item.filterTags)"
                :key="idx"
              >
                <CBadge
                  color="secondary"
                  class="mr-1"
                >{{ formatTag(tag) }}</CBadge>
              </span>
            </td>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment-timezone'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'

const formatDateTime = (date) => {
  return moment(date).format('MMM DD, YYYY, h:mm A')
}

export default {
  name: 'Frauds',

  title: 'Flagged Calls',

  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (e) => {
          if (!el.contains(e.target)) {
            binding.value()
          }
        }

        document.addEventListener('click', el._clickOutside)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  },

  data() {
    return {
      loading: false,
      showImportantOnly: false,
      tagDropdownOpen: false,
      statisticsExpanded: false,
      filterType: '',
      filterSeller: '',
      filterBuyer: '',
      filterCategory: '',
      filterTags: [],
      fields: [
        { key: 'callId', label: 'Call ID', _style: 'min-width: 160px;' },
        { key: 'receivedAt', label: 'Date', _style: 'min-width: 140px;' },
        { key: 'flagCategory', label: 'Type' },
        { key: 'sellerName', label: 'Seller' },
        { key: 'buyerName', label: 'Buyer' },
        { key: 'categoryName', label: 'Category Name' },
        { key: 'filterTags', label: 'Filter Tags', sorter: false },
      ],
    }
  },

  computed: {
    ...mapGetters('frauds', ['results', 'statistics', 'slackTeamId', 'slackFraudChannelId', 'slackSuspiciousChannelId']),

    typeOptions() {
      return [...new Set(this.results.map(r => {
        if (r.flagCategory === 0) {
          return 'Fraud'
        }

        if (r.flagCategory === 1) {
          return 'Suspicious'
        }

        return r.flagCategory
      }).filter(Boolean))].sort()
    },

    sellerOptions() {
      return [...new Set(this.results.map(r => r.sellerName).filter(Boolean))].sort()
    },

    buyerOptions() {
      return [...new Set(this.results.map(r => r.buyerName).filter(Boolean))].sort()
    },

    categoryOptions() {
      return [...new Set(this.results.map(r => r.categoryName).filter(Boolean))].sort()
    },

    tagOptions() {
      const tags = new Set()
      for (const r of this.results) {
        if (!r.filterTags) {
          continue
        }

        for (const t of r.filterTags.split(',').map(s => s.trim()).filter(Boolean)) {
          tags.add(t)
        }
      }

      return [...tags].sort()
    },

    filterTagsLabel() {
      if (this.filterTags.length === 0) {
        return 'None'
      }

      if (this.filterTags.length === this.tagOptions.length) {
        return 'All'
      }

      if (this.filterTags.length === 1) {
        return this.formatTag(this.filterTags[0])
      }

      return this.filterTags.length + ' selected'
    },

    hasActiveFilters() {
      return this.filterType !== ''
        || this.filterSeller !== ''
        || this.filterBuyer !== ''
        || this.filterCategory !== ''
        || this.filterTags.length !== this.tagOptions.length
    },

    filteredResults() {
      let items = this.results
      if (this.showImportantOnly) {
        items = items.filter(r => r.notifyAdmins)
      }

      if (this.filterType) {
        items = items.filter(r => {
          const label = r.flagCategory === 0 ? 'Fraud' : r.flagCategory === 1 ? 'Suspicious' : r.flagCategory
          return label === this.filterType
        })
      }

      if (this.filterSeller) {
        items = items.filter(r => r.sellerName === this.filterSeller)
      }

      if (this.filterBuyer) {
        items = items.filter(r => r.buyerName === this.filterBuyer)
      }

      if (this.filterCategory) {
        items = items.filter(r => r.categoryName === this.filterCategory)
      }

      items = items.filter(r => {
        const rowTags = this.splitTags(r.filterTags)
        return this.filterTags.some(t => rowTags.includes(t))
      })
      return items
    },
  },

  async created() {
    await this.handleFetch()
    this.filterTags = [...this.tagOptions]
  },

  methods: {
    ...mapActions('frauds', ['fetchResults']),
    formatDateTime,

    async handleFetch({ force = false } = {}) {
      this.loading = true
      try {
        await this.fetchResults({ force })
      } catch (error) {
        showErrorMessage('Failed to fetch fraud data')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    getSlackLink(flagCategory) {
      const isFraud = flagCategory === 'Fraud' || flagCategory === 0
      const channelId = isFraud ? this.slackFraudChannelId : this.slackSuspiciousChannelId
      return 'slack://channel?team=' + this.slackTeamId + '&id=' + channelId
    },

    clearFilters() {
      this.filterType = ''
      this.filterSeller = ''
      this.filterBuyer = ''
      this.filterCategory = ''
      this.filterTags = [...this.tagOptions]
    },

    truncateCallId(callId) {
      if (!callId || callId.length <= 16) {
        return callId
      }

      return callId.substring(0, 8) + '...'
    },

    copyCallId(callId) {
      navigator.clipboard.writeText(callId)
      showSuccessMessage('Call ID copied to clipboard')
    },

    formatTag(tag) {
      return tag.replace(/_/g, ' ')
    },

    splitTags(tags) {
      if (!tags) {
        return []
      }

      return tags.split(',').map(t => t.trim()).filter(t => t)
    },
  },
}
</script>

<style scoped>
.filters-row {
  margin-left: -5px;
  margin-right: -5px;
}
.filters-row > [class*="col"] {
  padding-left: 5px;
  padding-right: 5px;
}
</style>
